import { useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Box, Button, Menu, MenuItem, Typography } from '@mui/material';
import { Download, Upload, ArrowDropDown } from '@mui/icons-material';
import { RootState } from '../../store/store';
import {
  loadDraft,
  setPlannerError,
} from '../../store/slices/missionPlannerSlice';
import {
  toMissionFile,
  toKml,
  fromMissionFile,
} from '../../utils/missionFormat';
import { downloadTextFile } from '../../utils/download';
import { tutorialTarget } from '../../tutorial/tours';

/**
 * Export / import controls (issue #299): download the draft as a MAVLink
 * `.mission` (QGC WPL 110) or KML file, and import an existing `.mission` file
 * back into the planner. Backend save/load is deferred until the persistence
 * contract is agreed (see the issue's process note).
 */
export const MissionIOControls: React.FC = () => {
  const dispatch = useDispatch();
  const draft = useSelector((state: RootState) => state.missionPlanner.draft);
  const hasRoute = draft.waypoints.length > 0;

  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const closeMenu = () => setMenuAnchor(null);

  const exportMission = () => {
    downloadTextFile('mission.mission', toMissionFile(draft), 'text/plain');
    closeMenu();
  };

  const exportKml = () => {
    downloadTextFile(
      'mission.kml',
      toKml(draft),
      'application/vnd.google-earth.kml+xml'
    );
    closeMenu();
  };

  const onImportFile = async (
    event: React.ChangeEvent<HTMLInputElement>
  ): Promise<void> => {
    const file = event.target.files?.[0];
    // Reset so re-selecting the same file fires change again.
    event.target.value = '';
    if (!file) return;
    try {
      const text = await file.text();
      const { home, waypoints } = fromMissionFile(text);
      if (waypoints.length === 0) {
        throw new Error('Mission file contains no waypoints.');
      }
      dispatch(loadDraft({ waypoints, homeBase: home }));
    } catch (err) {
      dispatch(
        setPlannerError(
          `Import failed: ${err instanceof Error ? err.message : String(err)}`
        )
      );
    }
  };

  return (
    <Box
      component="section"
      aria-label="Mission import and export"
      sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}
    >
      <Typography variant="subtitle2" component="h3">
        Import &amp; export
      </Typography>
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button
          variant="outlined"
          size="small"
          startIcon={<Download />}
          endIcon={<ArrowDropDown />}
          onClick={(e) => setMenuAnchor(e.currentTarget)}
          disabled={!hasRoute}
          sx={{ flex: 1 }}
          {...tutorialTarget('planner-export')}
        >
          Export
        </Button>
        <Button
          variant="outlined"
          size="small"
          startIcon={<Upload />}
          onClick={() => fileInputRef.current?.click()}
          sx={{ flex: 1 }}
        >
          Import
        </Button>
      </Box>

      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeMenu}>
        <MenuItem onClick={exportMission}>MAVLink mission (.mission)</MenuItem>
        <MenuItem onClick={exportKml}>KML (.kml)</MenuItem>
      </Menu>

      <input
        ref={fileInputRef}
        type="file"
        accept=".mission,.waypoints,.txt"
        onChange={onImportFile}
        aria-label="Import mission file"
        style={{ display: 'none' }}
      />
    </Box>
  );
};
